import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureFrontendException } from "@iamu/telemetry/browser";
import { formatTrpcErrorMessage } from "@/lib/trpc-error";

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  title?: string;
  description?: string;
  onReset?: () => void;
  variant?: "fullscreen" | "panel" | "inline";
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    captureFrontendException(error, {
      "exception.type": "ReactErrorBoundary",
      "react.component_stack": info.componentStack,
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback(this.state.error, this.reset);
      }
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const title = this.props.title || "Something went wrong";
      const description =
        this.props.description ||
        formatTrpcErrorMessage(
          this.state.error,
          "An unexpected error occurred.",
        );

      const isPanel = this.props.variant === "panel";
      const isInline = this.props.variant === "inline";

      if (isInline) {
        return (
          <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs">
            <span className="text-destructive font-medium">{title}</span>
            <Button
              variant="outline"
              size="xs"
              onClick={this.reset}
              className="gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        );
      }

      return (
        <div
          className={
            isPanel
              ? "flex flex-col items-center justify-center gap-3 p-8 text-center"
              : "flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center"
          }
        >
          <div className="rounded-full bg-destructive/10 p-2.5 text-destructive">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          <p className="text-muted-foreground max-w-md text-sm">
            {description}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={this.reset}
              className="gap-2"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Try again
            </Button>
            {!isPanel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => (window.location.href = "/")}
              >
                Go home
              </Button>
            )}
          </div>
          <a
            href="mailto:support@iamu.dev"
            className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Contact support
          </a>
        </div>
      );
    }

    return this.props.children;
  }
}
